import {
  ComponentType,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useMatch, useNavigate, useSearchParams } from "react-router-dom";

import { Hide, Show, Tab, TabList, TabPanels, Tabs } from "@chakra-ui/react";
import { SingleValue } from "react-select";

import { Filter } from "components/Filters";
import { Select } from "components/Select";
import { SortSelectProps } from "components/SortTermsSelect";
import { useBackToTop } from "contexts/BackToTopProvider";
import RequestParamsProvider, {
  FilterStateValue,
} from "contexts/RequestParamsProvider";
import { SORT_DIRECTION } from "hooks/useSort";
import { removeNullable } from "services/apiHelpers";
import { MOBILE_BREAKPOINT_SIZE } from "util/styleHelpers";

import contractsDefaultFilters from "components/Filters/Contracts/contracts-default-filters";
import deliveriesDefaultFilters from "components/Filters/Deliveries/deliveries-default-filters";
import invoicesDefaultFilters from "components/Filters/Invoices/invoices-default-filters";
import ordersDefaultFilters from "components/Filters/Orders/orders-default-filters";
import { documentsPath } from "constants/paths";
import TabPanelWithSearch from "./TabPanelWithSearch";
import ContractsTable, { contractsColumns } from "./contracts/ContractsTable";
import contractsFilters from "./contracts/contracts-filters";
import DeliveriesTable, { deliveriesColumns } from "./deliveries";
import deliveriesFilters from "./deliveries/deliveries-filters";
import FilesTable from "./files/FilesTable";
import DocumentsInvoicesTable, {
  documentsInvoicesColumns,
} from "./invoices/DocumentsInvoicesTable";
import invoicesFilters from "./invoices/invoices-filters";
import DocumentsOrdersTable, {
  ordersColumns,
} from "./orders/DocumentsOrdersTable";
import ordersFilters from "./orders/orders-filters";
import { ExportReportParamsObjectTypeEnum } from "api";

enum DOCUMENT_PAGE_TAB_NAMES {
  CONTRACTS = "Contracts",
  ORDERS = "Orders",
  DELIVERIES = "Deliveries",
  INVOICES = "Invoices",
  FILES = "Files",
}

export type ComponentTabProps = {
  search?: string;
};

export type ComponentTab = {
  tabName?: string;
  component: ComponentType<ComponentTabProps>;
  filters: Filter[];
  defaultFilters?: { [key: string]: FilterStateValue };
  showSearch?: boolean;
  defaultSort?: { key: string; direction: SORT_DIRECTION };
  exportObjectType?: ExportReportParamsObjectTypeEnum;
} & Pick<SortSelectProps, "sortOptions">;

const columnDefinitionsToSortOptions = <
  T extends { sortKey?: string; columnLabel?: string }[],
>(
  columns: T
): SortSelectProps["sortOptions"] =>
  removeNullable(
    columns.map((column) =>
      column.sortKey && column.columnLabel
        ? {
            label: column.columnLabel,
            sortKey: column.sortKey,
          }
        : null
    )
  );

const documentsPageTables: ComponentTab[] = [
  {
    tabName: DOCUMENT_PAGE_TAB_NAMES.CONTRACTS,
    component: ContractsTable,
    filters: contractsFilters,
    defaultFilters: contractsDefaultFilters,
    defaultSort: { key: "DocEntry", direction: SORT_DIRECTION.DESC },
    sortOptions: columnDefinitionsToSortOptions(contractsColumns),
    exportObjectType: ExportReportParamsObjectTypeEnum.OQuotations,
  },
  {
    tabName: DOCUMENT_PAGE_TAB_NAMES.ORDERS,
    component: DocumentsOrdersTable,
    filters: ordersFilters,
    defaultFilters: ordersDefaultFilters,
    defaultSort: { key: "DocEntry", direction: SORT_DIRECTION.DESC },
    sortOptions: columnDefinitionsToSortOptions(ordersColumns),
    exportObjectType: ExportReportParamsObjectTypeEnum.OOrders,
  },
  {
    tabName: DOCUMENT_PAGE_TAB_NAMES.DELIVERIES,
    component: DeliveriesTable,
    filters: deliveriesFilters,
    defaultFilters: deliveriesDefaultFilters,
    defaultSort: { key: "DocEntry", direction: SORT_DIRECTION.DESC },
    sortOptions: columnDefinitionsToSortOptions(deliveriesColumns),
    exportObjectType: ExportReportParamsObjectTypeEnum.ODeliveryNotes,
  },
  {
    tabName: DOCUMENT_PAGE_TAB_NAMES.INVOICES,
    component: DocumentsInvoicesTable,
    filters: invoicesFilters,
    defaultFilters: invoicesDefaultFilters,
    defaultSort: { key: "DocEntry", direction: SORT_DIRECTION.DESC },
    sortOptions: columnDefinitionsToSortOptions(documentsInvoicesColumns),
    exportObjectType: ExportReportParamsObjectTypeEnum.OInvoices,
  },
  {
    tabName: DOCUMENT_PAGE_TAB_NAMES.FILES,
    component: FilesTable,
    filters: [],
    exportObjectType: undefined,
    showSearch: false,
  },
];

const tabPanels = documentsPageTables.map((tabParams) => (
  <RequestParamsProvider
    key={tabParams.tabName}
    initialSort={tabParams.defaultSort}
    initialFilters={tabParams.defaultFilters}
  >
    <TabPanelWithSearch {...tabParams} />
  </RequestParamsProvider>
));

const tabsList = documentsPageTables.map(({ tabName }) => (
  <Tab
    key={tabName}
    _selected={{
      color: "black",
      textDecorationLine: "underline",
      textUnderlineOffset: "15px",
    }}
    width="16%"
    textStyle="h4"
    color="grey.500"
    fontWeight="900"
  >
    {tabName}
  </Tab>
));

const tabSelectionOptions: SelectOption[] = documentsPageTables.map(
  ({ tabName }, index) => ({
    label: tabName ?? "",
    value: `${index}`,
  })
);

const DocumentsPageTabs = (): ReactElement => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const match = useMatch(`${documentsPath}/:tab`);
  const matchTab = match?.params.tab;

  const selectedTabIndex = useMemo(
    () =>
      documentsPageTables.findIndex(
        ({ tabName }) => tabName?.toLowerCase() === matchTab
      ),
    [matchTab]
  );

  const handleTabsChange = useCallback(
    (index: number, replace = false) => {
      navigate(
        `${documentsPageTables[index]?.tabName?.toLowerCase()}?${search}`,
        { replace }
      );
    },
    [navigate, search]
  );

  useEffect(() => {
    // No selected tab, redirect back to the first tab
    if (selectedTabIndex < 0) {
      handleTabsChange(0, true);
    }
  }, [handleTabsChange, selectedTabIndex]);

  const { scrollToTop } = useBackToTop();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, selectedTabIndex]);

  return (
    <Tabs
      onChange={handleTabsChange}
      flex={1}
      index={selectedTabIndex}
      colorScheme="black"
      display="flex"
      flexDirection="column"
      overflowX="hidden"
      isLazy
    >
      <Hide below={MOBILE_BREAKPOINT_SIZE}>
        <TabList
          width="1400px"
          borderTop="solid"
          borderTopColor="lightGrey"
          borderColor="lightGrey"
        >
          {tabsList}
        </TabList>
      </Hide>
      <Show below={MOBILE_BREAKPOINT_SIZE}>
        <Select
          size="sm"
          isSearchable={false}
          isClearable={false}
          options={tabSelectionOptions}
          value={tabSelectionOptions[selectedTabIndex]}
          onChange={
            ((option: SingleValue<SelectOption>) => {
              handleTabsChange(parseInt(option?.value ?? "0", 10));
            }) as any
          }
        />
      </Show>
      <TabPanels textAlign="center">{tabPanels}</TabPanels>
    </Tabs>
  );
};

export default DocumentsPageTabs;
